import {
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  SlideObject,
  SlideObjectStyle,
  resolveSlideFontFamily,
  resolvedFontWeight,
  resolvedLineHeight,
} from '../types';
import { DEFAULT_TEXT_PADDING } from './shapeFrame';

export const MIN_TEXT_BOX_W = 48;
export const MIN_TEXT_BOX_H = 36;
const CARET_SLACK_W = 10;
const CARET_SLACK_H = 6;

export function canAutoFitText(object: Pick<SlideObject, 'type' | 'locks'>): boolean {
  return object.type === 'text' && !object.locks.size;
}

export function hugsTextWidth(style?: SlideObjectStyle): boolean {
  return style?.textFit !== 'box';
}

export function resolveTextBoxSize(
  object: Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'type' | 'locks' | 'style'>,
  measured: { w: number; h: number },
  bounds?: { width: number; height: number },
): { w: number; h: number } | null {
  if (!canAutoFitText(object)) return null;
  const slideW = bounds?.width ?? SLIDE_WIDTH;
  const slideH = bounds?.height ?? SLIDE_HEIGHT;
  const maxW = Math.max(MIN_TEXT_BOX_W, slideW - object.x - 24);
  const maxH = Math.max(MIN_TEXT_BOX_H, slideH - object.y - 24);
  const w = hugsTextWidth(object.style)
    ? Math.min(maxW, Math.max(MIN_TEXT_BOX_W, Math.ceil(measured.w)))
    : object.w;
  const h = Math.min(maxH, Math.max(MIN_TEXT_BOX_H, Math.ceil(measured.h)));
  if (Math.abs(w - object.w) < 1 && Math.abs(h - object.h) < 1) return null;
  return { w, h };
}

export function measureHtmlTextSize(
  html: string,
  style: SlideObjectStyle | undefined,
  options: { maxWidth: number; hugWidth: boolean; currentWidth: number },
): { w: number; h: number } {
  const fontSize = style?.fontSize || 24;
  const lineHeight = resolvedLineHeight(style);
  const padding = style?.padding != null ? Number(style.padding) : DEFAULT_TEXT_PADDING;
  const fallbackH = Math.ceil(fontSize * lineHeight + padding * 2 + CARET_SLACK_H);
  const fallbackW = options.hugWidth ? Math.min(options.maxWidth, Math.max(MIN_TEXT_BOX_W, options.currentWidth)) : options.currentWidth;
  if (typeof document === 'undefined') return { w: fallbackW, h: fallbackH };

  const el = ensureMeasurer();
  el.style.fontFamily = resolveSlideFontFamily(style?.fontFamily);
  el.style.fontSize = `${fontSize}px`;
  el.style.fontWeight = String(resolvedFontWeight(style));
  el.style.fontStyle = style?.fontStyle === 'italic' ? 'italic' : 'normal';
  el.style.letterSpacing = style?.letterSpacing != null ? `${style.letterSpacing}px` : '0';
  el.style.lineHeight = String(lineHeight);
  el.style.textTransform = style?.textTransform || 'none';
  el.style.textAlign = style?.textAlign || 'left';
  el.style.padding = `${padding}px`;
  el.style.maxWidth = `${Math.max(MIN_TEXT_BOX_W, options.maxWidth)}px`;
  el.innerHTML = html || '<p><br></p>';

  if (options.hugWidth) {
    el.style.width = 'max-content';
  } else {
    el.style.width = `${Math.max(MIN_TEXT_BOX_W, options.currentWidth)}px`;
  }

  const rawW = el.scrollWidth;
  const rawH = el.scrollHeight;
  if (rawW < 2 && rawH < 2) return { w: fallbackW, h: fallbackH };
  const w = options.hugWidth
    ? Math.min(options.maxWidth, Math.max(MIN_TEXT_BOX_W, Math.ceil(rawW + CARET_SLACK_W)))
    : options.currentWidth;
  if (options.hugWidth) el.style.width = `${w}px`;
  const h = Math.max(MIN_TEXT_BOX_H, Math.ceil((options.hugWidth ? el.scrollHeight : rawH) + CARET_SLACK_H));
  return { w, h };
}

export function applyTextBoxFit(
  object: SlideObject,
  html?: string,
  bounds?: { width: number; height: number },
): SlideObject {
  if (!canAutoFitText(object)) return object;
  const slideW = bounds?.width ?? SLIDE_WIDTH;
  const maxWidth = Math.max(MIN_TEXT_BOX_W, slideW - object.x - 24);
  const measured = measureHtmlTextSize(html ?? object.content ?? '', object.style, {
    maxWidth,
    hugWidth: hugsTextWidth(object.style),
    currentWidth: object.w,
  });
  const next = resolveTextBoxSize(object, measured, bounds);
  return next ? { ...object, w: next.w, h: next.h } : object;
}

export function applyTextBoxFitAll(
  objects: SlideObject[],
  ids?: string[],
  bounds?: { width: number; height: number },
): SlideObject[] {
  const wanted = ids ? new Set(ids) : null;
  return objects.map((object) => (wanted && !wanted.has(object.id) ? object : applyTextBoxFit(object, undefined, bounds)));
}

function ensureMeasurer(): HTMLElement {
  const existing = document.getElementById('athena-pres-text-measure');
  if (existing) return existing;
  if (!document.getElementById('athena-pres-text-measure-css')) {
    const css = document.createElement('style');
    css.id = 'athena-pres-text-measure-css';
    css.textContent = [
      '#athena-pres-text-measure{box-sizing:border-box;white-space:pre-wrap;overflow-wrap:break-word;word-break:break-word;}',
      '#athena-pres-text-measure p,#athena-pres-text-measure h1,#athena-pres-text-measure h2,#athena-pres-text-measure h3,#athena-pres-text-measure li,#athena-pres-text-measure li p,#athena-pres-text-measure ul,#athena-pres-text-measure ol,#athena-pres-text-measure span{margin:0;padding:0;line-height:inherit;font-family:inherit;font-size:inherit;font-weight:inherit;letter-spacing:inherit;color:inherit;}',
      '#athena-pres-text-measure ul,#athena-pres-text-measure ol{display:block;padding:0 0 0 1.35em;list-style-position:outside;}',
      '#athena-pres-text-measure ul{list-style-type:disc;}',
      '#athena-pres-text-measure ol{list-style-type:decimal;}',
      '#athena-pres-text-measure li,#athena-pres-text-measure li p{display:list-item;}',
    ].join('');
    document.head.appendChild(css);
  }
  const el = document.createElement('div');
  el.id = 'athena-pres-text-measure';
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'absolute',
    left: '-10000px',
    top: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  document.body.appendChild(el);
  return el;
}
